import { Component, type ErrorInfo, type ReactNode } from 'react'
import { logout } from '../api/auth'
import { listAttachedPlatforms, detachPlatform } from '../api/platforms'
import type { AttachedPlatform } from '../api/auth'

interface Props {
  children: ReactNode
}

interface State {
  error: Error | null
  attached: AttachedPlatform[]
  loadingAttached: boolean
  busyAttachmentId: string | null
  signingOut: boolean
  actionError: string | null
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = {
    error: null,
    attached: [],
    loadingAttached: false,
    busyAttachmentId: null,
    signingOut: false,
    actionError: null,
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ErrorBoundary]', error, info)
  }

  loadAttached = async () => {
    this.setState({ loadingAttached: true, actionError: null })
    try {
      const session = await listAttachedPlatforms()
      this.setState({ attached: session.attached ?? [], loadingAttached: false })
    } catch (err) {
      this.setState({
        loadingAttached: false,
        actionError: err instanceof Error ? err.message : 'Failed to load platforms',
      })
    }
  }

  handleDetach = async (attachmentId: string) => {
    this.setState({ busyAttachmentId: attachmentId, actionError: null })
    try {
      await detachPlatform(attachmentId)
      window.location.reload()
    } catch (err) {
      this.setState({
        busyAttachmentId: null,
        actionError: err instanceof Error ? err.message : 'Detach failed',
      })
    }
  }

  handleSignOut = async () => {
    this.setState({ signingOut: true, actionError: null })
    try {
      await logout()
    } catch {
      // Even if logout fails server-side, send the user to the root so the
      // SPA re-bootstraps and re-queries session state.
    } finally {
      window.location.assign('/')
    }
  }

  render() {
    const { error } = this.state
    if (!error) return this.props.children

    const { attached, loadingAttached, busyAttachmentId, signingOut, actionError } = this.state

    return (
      <div className="min-h-screen flex items-center justify-center px-4 bg-zinc-950">
        <div className="w-full max-w-md bg-zinc-900 border border-zinc-800 rounded-lg p-6 shadow-xl">
          <h1 className="text-2xl font-semibold text-zinc-100 mb-1">Something went wrong</h1>
          <p className="text-sm text-zinc-400 mb-4">
            The app couldn't render. This is often caused by a newly-attached platform returning
            data the UI doesn't recognize. Detaching that platform usually unblocks the app.
          </p>

          <pre className="whitespace-pre-wrap break-all text-xs text-zinc-500 bg-zinc-950 border border-zinc-800 rounded p-3 mb-4">
            {error.name}: {error.message}
          </pre>

          {actionError && (
            <div className="mb-4 text-sm text-red-400 bg-red-950/40 border border-red-900 rounded p-3 whitespace-pre-wrap break-all">
              {actionError}
            </div>
          )}

          {attached.length > 0 && (
            <ul className="mb-4 divide-y divide-zinc-800 border border-zinc-800 rounded">
              {attached.map((p) => (
                <li key={p.id} className="flex items-center justify-between px-3 py-2">
                  <div className="min-w-0">
                    <div className="text-sm text-zinc-200 truncate">{p.displayName}</div>
                    <div className="text-xs text-zinc-500 truncate">{p.platformId}</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => this.handleDetach(p.id)}
                    disabled={busyAttachmentId !== null}
                    className="ml-3 text-xs text-red-400 hover:text-red-300 disabled:text-zinc-600"
                  >
                    {busyAttachmentId === p.id ? 'Detaching…' : 'Detach'}
                  </button>
                </li>
              ))}
            </ul>
          )}

          <button
            type="button"
            onClick={() => window.location.reload()}
            className="w-full bg-emerald-600 hover:bg-emerald-500 text-white font-medium py-2 rounded transition"
          >
            Reload
          </button>

          <button
            type="button"
            onClick={this.loadAttached}
            disabled={loadingAttached || busyAttachmentId !== null}
            className="mt-3 w-full border border-zinc-700 hover:border-zinc-600 text-zinc-200 text-sm py-2 rounded transition disabled:text-zinc-500"
          >
            {loadingAttached
              ? 'Loading platforms…'
              : attached.length > 0
                ? 'Refresh platform list'
                : 'Detach a platform'}
          </button>

          <button
            type="button"
            onClick={this.handleSignOut}
            disabled={signingOut}
            className="mt-3 w-full text-sm text-zinc-400 hover:text-zinc-100 disabled:text-zinc-600 py-2"
          >
            {signingOut ? 'Signing out…' : 'Sign out'}
          </button>
        </div>
      </div>
    )
  }
}

export default ErrorBoundary
